import { Box, Checkbox, Typography } from "@mui/material";
import FormOptionCard, { FormOptionCardConfig } from "app/components/FormOptionCard";
import { useStyles as useCardStyles } from "app/components/FormOptionCard/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectQuotationBoxStructure } from "../../../redux/selector";
import { actions } from "../../../redux/slice";
import { AddOnItemFormValue, QuotationBoxStructureState } from "../../../types";
import { useStyles as useAddOnStyles } from "../AddOn/styles";
import { QUOTATION_FORM_I18N_PREFIX, QUOTATION_SECTION_KEYS } from "../data";
import { FormFieldControl, FormSection } from "../VehicleDetails/FormControls";
import QuotationInputCard from "./QuotationInputCard";
import { useStyles } from "./styles";

type CardKey = Exclude<keyof QuotationBoxStructureState, "frontWall" | "backWall" | "frontRack" | "backRack">;
const P = `${QUOTATION_FORM_I18N_PREFIX}.BOX_STRUCTURE`;
const tKey = (suffix: string) => `${P}.${suffix}`;
const typeSelect = { type: "select" as const, options: [["LOW_TIN_PIPES", tKey("OPTIONS.LOW_TIN_PIPES")]] as [string, string][] };

const CARDS: Record<CardKey, FormOptionCardConfig> = {
  sideDoors: {
    titleKey: tKey("CARDS.SIDE_DOORS"), layout: "horizontal", fields: ["quantity", "note"],
    notePlaceholderKey: tKey("FIELDS.NOTE"), quantityLabelKey: tKey("FIELDS.MISSING_QUANTITY"),
  },
  pillars: {
    titleKey: tKey("CARDS.PILLARS"), layout: "horizontal", fields: ["radio", "quantity"],
    radioLabelKey: tKey("FIELDS.TYPE"), quantityLabelKey: tKey("FIELDS.QUANTITY"),
    radioOptions: [{ value: "GAS_MODEL", labelKey: tKey("OPTIONS.GAS_MODEL") }],
  },
  hydraulicSide: {
    titleKey: tKey("CARDS.HYDRAULIC_SIDE"), fields: ["radio"], radioLabelKey: tKey("FIELDS.TYPE"),
    radioOptions: [{ value: "STANDARD", labelKey: tKey("OPTIONS.STANDARD") }],
  },
  rearDoor: { titleKey: tKey("CARDS.REAR_DOOR"), fields: ["note"], notePlaceholderKey: tKey("FIELDS.NOTE") },
};

const BoxStructurePanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const boxStructure = useSelector(selectQuotationBoxStructure);
  const styles = useStyles();
  const addOnStyles = useAddOnStyles();
  const cardStyles = useCardStyles();
  if (!boxStructure) return null;

  const labels = {
    note: tKey("FIELDS.NOTE"), quantity: tKey("FIELDS.QUANTITY"), size: tKey("FIELDS.HEIGHT_MM"),
    location: tKey("FIELDS.HEIGHT_OPTION"), itemType: tKey("FIELDS.TYPE"),
  };
  const update = (key: keyof QuotationBoxStructureState, value: Partial<AddOnItemFormValue> | Partial<QuotationBoxStructureState["frontWall"]>) =>
    dispatch(actions.updateQuotationBoxStructure({ key, value }));
  const row = (cols: 1 | 2, children: React.ReactNode) => (
    <Box className={`${addOnStyles.cardRow} ${cols === 1 ? addOnStyles.cardRow1 : addOnStyles.cardRow2}`}>{children}</Box>
  );
  const renderCard = (key: CardKey) => (
    <FormOptionCard key={key} config={CARDS[key]} value={boxStructure[key]} labels={labels} onChange={(v) => update(key, v)} />
  );

  const { frontWall, backWall, frontRack, backRack } = boxStructure;
  const noteCol = (checked: boolean, label: string, onCheck: (v: boolean) => void, noteVal: string, onNote: (v: string) => void) => (
    <Box className={cardStyles.fieldsColumn} style={{ flex: 1 }}>
      <Box className={cardStyles.subCheckboxRow}>
        <Checkbox checked={checked} sx={{ p: 0 }} onChange={(e) => onCheck(e.target.checked)} />
        <Typography className={cardStyles.subCheckboxLabel}>{t(label)}</Typography>
      </Box>
      <FormFieldControl config={{ field: "note", labelKey: tKey("FIELDS.NOTE"), type: "text" }}
        value={noteVal} onChange={onNote} />
    </Box>
  );

  return (
    <QuotationInputCard titleKey={QUOTATION_SECTION_KEYS.BOX_STRUCTURE}>
      <FormSection titleKey={tKey("SECTIONS.WALLS")} hideTopBorder titleClassName={styles.sectionTitle}>
        <Box className={addOnStyles.sectionContent}>
          {row(1, (
            <Box className={`${cardStyles.card} ${frontWall.enabled ? cardStyles.cardActive : ""}`}>
              <Box className={cardStyles.cardHeader}>
                <Checkbox checked={frontWall.enabled} sx={{ p: 0 }} onChange={(e) => update("frontWall", { enabled: e.target.checked })} />
                <Typography className={cardStyles.cardTitle}>{t(tKey("CARDS.FRONT_WALL"))}</Typography>
              </Box>
              <Box className={cardStyles.cardDivider} />
              <Box className={cardStyles.fieldsRow}>
                <FormFieldControl config={{ field: "selectedOption", labelKey: tKey("FIELDS.TYPE"), ...typeSelect }}
                  value={frontWall.selectedOption} onChange={(v) => update("frontWall", { selectedOption: v })} />
                <FormFieldControl config={{ field: "height", labelKey: tKey("FIELDS.HEIGHT_MM"), type: "number", showSpinners: true, narrow: true }}
                  value={frontWall.height} onChange={(v) => update("frontWall", { height: v })} />
              </Box>
              <Box className={cardStyles.fieldsRow}>
                {noteCol(frontWall.treadSurface, tKey("FIELDS.TREAD_SURFACE"), (v) => update("frontWall", { treadSurface: v }), frontWall.treadNote, (v) => update("frontWall", { treadNote: v }))}
                {noteCol(frontWall.widthReinforcement, tKey("FIELDS.WIDTH_REINFORCEMENT"), (v) => update("frontWall", { widthReinforcement: v }), frontWall.widthNote, (v) => update("frontWall", { widthNote: v }))}
              </Box>
            </Box>
          ))}
          {row(2, (<>
            <Box className={`${cardStyles.card} ${frontRack.enabled || backRack.enabled ? cardStyles.cardActive : ""}`}>
              <Box className={cardStyles.cardHeader}>
                <Checkbox checked={frontRack.enabled} sx={{ p: 0 }} onChange={(e) => update("frontRack", { enabled: e.target.checked })} />
                <Typography className={cardStyles.cardTitle}>{t(tKey("CARDS.FRONT_RACK"))}</Typography>
              </Box>
              <Box className={cardStyles.cardDivider} />
              <FormFieldControl config={{ field: "location", labelKey: tKey("FIELDS.HEIGHT_OPTION"), type: "text" }}
                value={frontRack.location} onChange={(v) => update("frontRack", { location: v })} />
              <Box className={cardStyles.cardHeader} sx={{ pt: 1 }}>
                <Checkbox checked={backRack.enabled} sx={{ p: 0 }} onChange={(e) => update("backRack", { enabled: e.target.checked })} />
                <Typography className={cardStyles.cardTitle}>{t(tKey("CARDS.BACK_RACK"))}</Typography>
              </Box>
              <Box className={cardStyles.cardDivider} />
              <Box className={cardStyles.fieldsRow}>
                <FormFieldControl config={{ field: "location", labelKey: tKey("FIELDS.HEIGHT_OPTION"), type: "text" }}
                  value={backRack.location} onChange={(v) => update("backRack", { location: v })} />
                <FormFieldControl config={{ field: "note", labelKey: tKey("FIELDS.NOTE"), type: "text" }}
                  value={backRack.note} onChange={(v) => update("backRack", { note: v })} />
              </Box>
            </Box>
            <Box className={`${cardStyles.card} ${backWall.enabled ? cardStyles.cardActive : ""}`}>
              <Box className={cardStyles.cardHeader}>
                <Checkbox checked={backWall.enabled} sx={{ p: 0 }} onChange={(e) => update("backWall", { enabled: e.target.checked })} />
                <Typography className={cardStyles.cardTitle}>{t(tKey("CARDS.BACK_WALL"))}</Typography>
              </Box>
              <Box className={cardStyles.cardDivider} />
              <Box className={cardStyles.fieldsRow}>
                <FormFieldControl config={{ field: "selectedOption", labelKey: tKey("FIELDS.TYPE"), ...typeSelect }}
                  value={backWall.selectedOption} onChange={(v) => update("backWall", { selectedOption: v })} />
                <FormFieldControl config={{ field: "size", labelKey: tKey("FIELDS.HEIGHT_MM"), type: "number", showSpinners: true, narrow: true }}
                  value={backWall.size} onChange={(v) => update("backWall", { size: v })} />
              </Box>
              <Box className={cardStyles.subCheckboxRow}>
                <Checkbox checked={backWall.optionEnabled} sx={{ p: 0 }} onChange={(e) => update("backWall", { optionEnabled: e.target.checked })} />
                <Typography className={cardStyles.subCheckboxLabel}>{t(tKey("FIELDS.WIDTH_REINFORCEMENT"))}</Typography>
              </Box>
              <FormFieldControl config={{ field: "note", labelKey: tKey("FIELDS.NOTE"), type: "text" }}
                value={backWall.note} onChange={(v) => update("backWall", { note: v })} />
            </Box>
          </>))}
        </Box>
      </FormSection>
      <FormSection titleKey={tKey("SECTIONS.DOORS")} titleClassName={styles.sectionTitle}>
        <Box className={addOnStyles.sectionContent}>
          {row(2, (<>{renderCard("pillars")}{renderCard("sideDoors")}</>))}
          {row(2, (<>{renderCard("rearDoor")}{renderCard("hydraulicSide")}</>))}
        </Box>
      </FormSection>
    </QuotationInputCard>
  );
};

export default BoxStructurePanel;
